// WARNING: THIS FILE IS SHARED TO THE CLIENT

/**
 * Public (client-side) Firebase configs — the single source for both the
 * client bundle (client/src/Firebase.js) and every server endpoint that
 * hands a config to a browser: /__/firebase/init.json, /mobile-plans-auth,
 * and the /api/development/* inline login pages. Public keys only, safe to
 * serve. Do NOT read them from firebaseAdmin.apps[0].options, which would
 * expose the private key.
 */

export type FirebaseClientConfig = {
  apiKey: string;
  authDomain: string;
  databaseURL: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

export const FIREBASE_CLIENT_CONFIG_DEV: FirebaseClientConfig = {
  apiKey: "AIzaSyDiBYLh66GBJVVpT4J19f8TZ6Tp2ymf7rM",
  authDomain: "sudowrite-development.firebaseapp.com",
  databaseURL: "https://sudowrite-development.firebaseio.com",
  projectId: "sudowrite-development",
  storageBucket: "sudowrite-development.appspot.com",
  messagingSenderId: "102590229098",
  appId: "1:102590229098:web:6011e6a397a3ecab767813",
};

export const FIREBASE_CLIENT_CONFIG_PROD: FirebaseClientConfig = {
  apiKey: "AIzaSyCaNN59Os2Jg_wXU9uglyeW5Brz3tFEGYw",
  authDomain: "editor.sudowrite.com",
  databaseURL: "https://sudowrite-65e9a.firebaseio.com",
  projectId: "sudowrite-65e9a",
  storageBucket: "sudowrite-65e9a.appspot.com",
  messagingSenderId: "392167288218",
  appId: "1:392167288218:web:288d037bf50ded7542de23",
};

/**
 * Prod authDomain for contexts that can't use editor.sudowrite.com: OAuth
 * flows need authDomain to be a registered redirect origin, and PR-preview /
 * staging hosts aren't whitelisted — they fall back to the project's own
 * firebaseapp.com domain (see client/src/Firebase.js isStagingBranch).
 */
export const FIREBASE_PROD_FALLBACK_AUTH_DOMAIN = "sudowrite-65e9a.firebaseapp.com";

/**
 * Full config for the self-hosted Firebase auth handlers (/__/firebase/init.json).
 */
export const initJsonFirebaseConfig = (host: string | undefined): FirebaseClientConfig => {
  if (process.env.NODE_ENV === "development" || process.env.REACT_APP_NODE_ENV === "development") {
    return FIREBASE_CLIENT_CONFIG_DEV;
  }
  return host?.match(/sudowrite-pr-\d+\.onrender\.com/)
    ? { ...FIREBASE_CLIENT_CONFIG_PROD, authDomain: FIREBASE_PROD_FALLBACK_AUTH_DOMAIN }
    : FIREBASE_CLIENT_CONFIG_PROD;
};

/**
 * Minimal config for server-rendered pages that sign in with a custom token
 * (/mobile-plans-auth, the /api/development/* login pages). Must target the
 * same Firebase project the admin SDK mints tokens for — src/firebase.js
 * picks its service account by NODE_ENV === "production" — or
 * signInWithCustomToken fails with "custom token corresponds to a different
 * audience". Render PR previews run NODE_ENV=production, so they get the
 * prod config even where dev routes are enabled. authDomain is unused by the
 * custom-token flow (no OAuth redirect) but included for completeness.
 */
export const customTokenSignInConfig = (): Pick<FirebaseClientConfig, "apiKey" | "authDomain" | "projectId"> => {
  const { apiKey, authDomain, projectId } =
    process.env.NODE_ENV === "production" ? FIREBASE_CLIENT_CONFIG_PROD : FIREBASE_CLIENT_CONFIG_DEV;
  return { apiKey, authDomain, projectId };
};
